import { useMutation } from '@apollo/client'
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    Stack,
    TextField,
    Typography,
} from '@mui/material'
import { useFormik } from 'formik'
import { useState } from 'react'
import { AddRecruiter, EditRecruiter } from 'recruiter/graphql/admin.gql'
import useToast from 'shared/components/Toast/useToast'
import { formatGraphQLError, getPxAsRem } from 'shared/utils'
import * as Yup from 'yup'

export interface RecruiterToEdit {
    recruiterId: string
    email: string
    firstName: string
    lastName: string
}

interface RecruiterModalProps {
    open: boolean
    recruiter: RecruiterToEdit | null
    onCancel: () => void
    refetch: () => void
}

const validationSchema = Yup.object({
    email: Yup.string().required('Email is required').email('Invalid email'),
    firstName: Yup.string().required('First name is required').trim().strict(),
    lastName: Yup.string().required('Last name is required').trim().strict(),
})

export default function RecruiterModal({
    open,
    recruiter,
    onCancel,
    refetch,
}: RecruiterModalProps) {
    const isEdit = recruiter !== null
    const [addRecruiter] = useMutation(AddRecruiter)
    const [editRecruiter] = useMutation(EditRecruiter)
    const [loading, setLoading] = useState(false)
    const toast = useToast()

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            email: recruiter?.email ?? '',
            firstName: recruiter?.firstName ?? '',
            lastName: recruiter?.lastName ?? '',
        },
        validateOnBlur: true,
        validateOnChange: true,
        validateOnMount: false,
        validationSchema,
        onSubmit: async (values) => {
            setLoading(true)
            const trimmed = {
                email: values.email.trim(),
                firstName: values.firstName.trim(),
                lastName: values.lastName.trim(),
            }
            if (isEdit) {
                editRecruiter({
                    variables: {
                        recruiterId: recruiter.recruiterId,
                        ...trimmed,
                    },
                    onCompleted: (data) => {
                        if (data.editRecruiter.success) {
                            toast({
                                title: 'Success',
                                severity: 'success',
                                message:
                                    data.editRecruiter.message ??
                                    'Recruiter updated',
                            })
                            refetch()
                            onClose()
                        } else {
                            toast({
                                title: 'Error',
                                severity: 'error',
                                message:
                                    data.editRecruiter.message ??
                                    'Failed to update recruiter',
                            })
                        }
                        setLoading(false)
                    },
                    onError: (error) => {
                        toast({
                            title: 'Error',
                            severity: 'error',
                            message: formatGraphQLError(
                                error,
                                'Failed to update recruiter'
                            ),
                        })
                        setLoading(false)
                    },
                })
            } else {
                addRecruiter({
                    variables: trimmed,
                    onCompleted: (data) => {
                        if (data.addRecruiter.success) {
                            toast({
                                title: 'Success',
                                severity: 'success',
                                message:
                                    data.addRecruiter.message ??
                                    'Recruiter added',
                            })
                            refetch()
                            onClose()
                        } else {
                            toast({
                                title: 'Error',
                                severity: 'error',
                                message:
                                    data.addRecruiter.message ??
                                    'Failed to add recruiter',
                            })
                        }
                        setLoading(false)
                    },
                    onError: (error) => {
                        toast({
                            title: 'Error',
                            severity: 'error',
                            message: formatGraphQLError(
                                error,
                                'Failed to add recruiter'
                            ),
                        })
                        setLoading(false)
                    },
                })
            }
        },
    })

    const onClose = () => {
        formik.resetForm()
        onCancel()
    }

    return (
        <Dialog onClose={onClose} open={open}>
            <Stack
                direction="column"
                spacing={2}
                alignItems="flex-start"
                justifyContent="space-between"
                display="flex"
                sx={{
                    padding: getPxAsRem(16, 24),
                    minWidth: getPxAsRem(500),
                    minHeight: getPxAsRem(300),
                }}
            >
                <Typography variant="h4">
                    {isEdit ? 'Edit Recruiter' : 'Add Recruiter'}
                </Typography>
                <TextField
                    variant="standard"
                    fullWidth
                    label="Email"
                    name="email"
                    value={formik.values.email}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.email && Boolean(formik.errors.email)}
                    helperText={formik.touched.email && formik.errors.email}
                    required
                    InputLabelProps={{ shrink: true }}
                />
                <Stack direction="row" spacing={2} width="100%">
                    <TextField
                        variant="standard"
                        fullWidth
                        label="First Name"
                        name="firstName"
                        value={formik.values.firstName}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={
                            formik.touched.firstName &&
                            Boolean(formik.errors.firstName)
                        }
                        helperText={
                            formik.touched.firstName && formik.errors.firstName
                        }
                        required
                        InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                        variant="standard"
                        fullWidth
                        label="Last Name"
                        name="lastName"
                        value={formik.values.lastName}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={
                            formik.touched.lastName &&
                            Boolean(formik.errors.lastName)
                        }
                        helperText={
                            formik.touched.lastName && formik.errors.lastName
                        }
                        required
                        InputLabelProps={{ shrink: true }}
                    />
                </Stack>
                <Box width="100%" justifyContent="end" alignItems="end">
                    <DialogActions sx={{ padding: getPxAsRem(8) }}>
                        <Button variant="text" onClick={onClose}>
                            Cancel
                        </Button>
                        <Button
                            disabled={loading}
                            onClick={() => formik.handleSubmit()}
                        >
                            {isEdit ? 'Save' : 'Add'}
                        </Button>
                    </DialogActions>
                </Box>
            </Stack>
        </Dialog>
    )
}
